import { z } from 'zod';
import { DateTimeSchema, ObjectIdString } from '../../common.js';
import { FieldFilter, DateFilter } from '../../common.js';
import { BaseOperationStepResult, StepConfig, TypeConfig } from './operationSharedTypes.js';

export const DiscoveryOperationType = z.enum([
  'discoverFields',
  'discoverNoteActions',
  'discoverFieldExclusions',
]);
export type DiscoveryOperationType = z.infer<typeof DiscoveryOperationType>;

export const DiscoveryOperationStepName = z.enum([
  'discoverFieldsInitialize',
  'discoverFieldsProcessBatch',
  'discoverFieldsFinalize',
  'discoverNoteActionsInitialize',
  'discoverNoteActionsProcessBatch',
  'discoverNoteActionsFinalize',
  'discoverFieldExclusionsInitialize',
  'discoverFieldExclusionsProcessEntity',
  'discoverFieldExclusionsFinalize',
]);
export type DiscoveryOperationStepName = z.infer<typeof DiscoveryOperationStepName>;

//--------------------------------------------------------------------------------------------------------------------//
//region Discovery Operation Input Options
export const BaseDiscoveryInput = z.object({
  exampleCount: z.number().int().min(1).max(20).default(5),
  dateFilter: DateFilter.nullish(),
});
export type BaseDiscoveryInput = z.infer<typeof BaseDiscoveryInput>;

export const FieldDiscoveryInput = z.object({
  ...BaseDiscoveryInput.shape,
  fieldFilters: FieldFilter.array().nullish(),
  limitToFields: z.string().array().nullish(), // For testing
});
export type FieldDiscoveryInput = z.infer<typeof FieldDiscoveryInput>;

export const DiscoveryField = z.object({
  name: z.string(),
  label: z.string(),
  type: z.string(),
  dataType: z.string().optional(),
});
export type DiscoveryField = z.infer<typeof DiscoveryField>;

export const FieldDiscoveryProcessBatchOptions = z.object({
  fields: DiscoveryField.array(),
  batchIndex: z.number(),
  totalBatches: z.number(),
  totalCandidates: z.number(),
  exampleCount: z.number(),
  candidateFilterQuery: z.string(),
});
export type FieldDiscoveryProcessBatchOptions = z.infer<typeof FieldDiscoveryProcessBatchOptions>;

export const FieldDiscoveryFinalizeOptions = z.object({});
export type FieldDiscoveryFinalizeOptions = z.infer<typeof FieldDiscoveryFinalizeOptions>;

export const NoteActionsDiscoveryInput = z.object({
  ...BaseDiscoveryInput.shape,
});
export type NoteActionsDiscoveryInput = z.infer<typeof NoteActionsDiscoveryInput>;

export const NoteActionsDiscoveryProcessBatchOptions = z.object({
  actions: z.string().array(),
  batchIndex: z.number(),
  totalBatches: z.number(),
  totalNotes: z.number(),
  exampleCount: z.number(),
  dateFilterQuery: z.string().nullish(),
});
export type NoteActionsDiscoveryProcessBatchOptions = z.infer<
  typeof NoteActionsDiscoveryProcessBatchOptions
>;

export const NoteActionsDiscoveryFinalizeOptions = z.object({});
export type NoteActionsDiscoveryFinalizeOptions = z.infer<
  typeof NoteActionsDiscoveryFinalizeOptions
>;

// Entities supported for field exclusion discovery (subset of BullhornEntityName)
// TODO: support all Bullhorn entities and just make this list the default?
export const FieldExclusionEntity = z.enum([
  'Candidate',
  'CorporateUser',
  'Note',
  'JobOrder',
  'Placement',
  'JobSubmission',
  'ClientContact',
  'ClientCorporation',
]);
export type FieldExclusionEntity = z.infer<typeof FieldExclusionEntity>;

// Field Exclusions Discovery Input - accountId required by bullhornProcedure
export const FieldExclusionsDiscoveryInput = z.object({
  entityList: FieldExclusionEntity.array().nullish(),
});
export type FieldExclusionsDiscoveryInput = z.infer<typeof FieldExclusionsDiscoveryInput>;

export const FieldExclusionsProcessEntityOptions = z.object({
  entity: FieldExclusionEntity,
  entityIndex: z.number(),
  totalEntities: z.number(),
});
export type FieldExclusionsProcessEntityOptions = z.infer<
  typeof FieldExclusionsProcessEntityOptions
>;

export const FieldExclusionsFinalizeOptions = z.object({});
export type FieldExclusionsFinalizeOptions = z.infer<typeof FieldExclusionsFinalizeOptions>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region Discovery Step Results

export const FieldDiscoveryInitializeResult = z.object({
  ...BaseOperationStepResult.shape,
  fieldsToProcess: z.number(),
  totalCandidates: z.number(),
  candidateFilterQuery: z.string(),
  numBatches: z.number(),
  fieldBatches: DiscoveryField.array().array().optional(),
});
export type FieldDiscoveryInitializeResult = z.infer<typeof FieldDiscoveryInitializeResult>;

export const DiscoveryFieldResult = z.object({
  ...DiscoveryField.shape,
  populatedCount: z.number().nullable(), // null when field couldn't be queried
  populationPercentage: z.number().nullable(), // null when field couldn't be queried
  exampleValues: z.coerce.string().array(),
  error: z.string().optional(),
});
export type DiscoveryFieldResult = z.infer<typeof DiscoveryFieldResult>;

export const FieldDiscoveryProcessBatchResult = z.object({
  ...BaseOperationStepResult.shape,
  batchIndex: z.number(),
  processedFields: z.number(),
  fields: DiscoveryFieldResult.array(),
});
export type FieldDiscoveryProcessBatchResult = z.infer<typeof FieldDiscoveryProcessBatchResult>;

export const FieldDiscoveryFinalizeResult = z.object({
  ...BaseOperationStepResult.shape,
  totalCandidates: z.number(),
  dateFilter: DateFilter.optional(),
  fieldFilters: FieldFilter.array().optional(),
  fieldsAnalyzed: z.number(),
  fields: DiscoveryFieldResult.array(),
});
export type FieldDiscoveryFinalizeResult = z.infer<typeof FieldDiscoveryFinalizeResult>;

export const NoteActionsDiscoveryInitializeResult = z.object({
  ...BaseOperationStepResult.shape,
  actionsToProcess: z.number(),
  totalNotes: z.number(),
  dateFilterQuery: z.string().nullish(),
  numBatches: z.number(),
  actionBatches: z.array(z.array(z.string())).optional(),
});
export type NoteActionsDiscoveryInitializeResult = z.infer<
  typeof NoteActionsDiscoveryInitializeResult
>;

export const NoteActionDiscoveryResultExample = z.object({
  id: z.coerce.string(),
  timestamp: DateTimeSchema,
  content: z.string(),
  candidate: z.object({
    id: z.coerce.string(),
    name: z.string().optional(),
  }),
  author: z
    .object({
      firstName: z.string().optional(),
      lastName: z.string().optional(),
    })
    .optional(),
});
export type NoteActionDiscoveryResultExample = z.infer<typeof NoteActionDiscoveryResultExample>;

export const NoteActionDiscoveryResultAction = z.object({
  action: z.string(),
  count: z.number(),
  percentage: z.number(),
  examples: NoteActionDiscoveryResultExample.array(),
  error: z.string().optional(),
});
export type NoteActionDiscoveryResultAction = z.infer<typeof NoteActionDiscoveryResultAction>;

export const NoteActionsDiscoveryProcessBatchResult = z.object({
  ...BaseOperationStepResult.shape,
  batchIndex: z.number(),
  processedActions: z.number(),
  actions: NoteActionDiscoveryResultAction.array(),
});
export type NoteActionsDiscoveryProcessBatchResult = z.infer<
  typeof NoteActionsDiscoveryProcessBatchResult
>;

export const NoteActionsDiscoveryFinalizeResult = z.object({
  ...BaseOperationStepResult.shape,
  totalNotes: z.number(),
  dateFilter: z.string().optional(),
  actions: NoteActionDiscoveryResultAction.array(),
});
export type NoteActionsDiscoveryFinalizeResult = z.infer<typeof NoteActionsDiscoveryFinalizeResult>;

// Field Exclusions Discovery Results
export const DiscoveredFieldExclusion = z.object({
  entity: FieldExclusionEntity,
  field: z.string(),
  errorMessage: z.string().optional(),
  isNew: z.boolean(),
  verified: z.boolean().optional(),
});
export type DiscoveredFieldExclusion = z.infer<typeof DiscoveredFieldExclusion>;

export const FieldExclusionsInitializeResult = z.object({
  ...BaseOperationStepResult.shape,
  entitiesToProcess: z.number(),
  entityList: FieldExclusionEntity.array(),
});
export type FieldExclusionsInitializeResult = z.infer<typeof FieldExclusionsInitializeResult>;

export const FieldExclusionsProcessEntityResult = z.object({
  ...BaseOperationStepResult.shape,
  entity: FieldExclusionEntity,
  entityIndex: z.number(),
  discoveredExclusions: DiscoveredFieldExclusion.array(),
  fieldsChecked: z.number(),
});
export type FieldExclusionsProcessEntityResult = z.infer<typeof FieldExclusionsProcessEntityResult>;

export const FieldExclusionsFinalizeResult = z.object({
  ...BaseOperationStepResult.shape,
  totalEntitiesChecked: z.number(),
  totalFieldsChecked: z.number(),
  totalNewExclusions: z.number(),
  // Default to empty object since MongoDB strips empty objects
  exclusionsByEntity: z.record(z.string(), DiscoveredFieldExclusion.array()).default({}),
});
export type FieldExclusionsFinalizeResult = z.infer<typeof FieldExclusionsFinalizeResult>;

// Input for applying discovered field exclusions
export const ApplyFieldExclusionsInput = z.object({
  accountId: ObjectIdString.nullish(),
  exclusions: DiscoveredFieldExclusion.array(),
});
export type ApplyFieldExclusionsInput = z.infer<typeof ApplyFieldExclusionsInput>;

export const ApplyFieldExclusionsResult = z.object({
  appliedCount: z.number(),
  exclusionsByEntity: z.record(z.string(), z.string().array()),
});
export type ApplyFieldExclusionsResult = z.infer<typeof ApplyFieldExclusionsResult>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//

export const discoveryOperationConfigs = {
  discoverFields: {
    steps: [
      'discoverFieldsInitialize',
      'discoverFieldsProcessBatch', // Can be run multiple times
      'discoverFieldsFinalize',
    ],
    hasChildOperations: false, // Sequential batch processing
  },
  discoverNoteActions: {
    steps: [
      'discoverNoteActionsInitialize',
      'discoverNoteActionsProcessBatch', // Can be run multiple times
      'discoverNoteActionsFinalize',
    ],
    hasChildOperations: false, // Sequential batch processing
  },
  discoverFieldExclusions: {
    steps: [
      'discoverFieldExclusionsInitialize',
      'discoverFieldExclusionsProcessEntity', // Repeatable for each entity
      'discoverFieldExclusionsFinalize',
    ],
    hasChildOperations: false,
  },
} as const satisfies Record<DiscoveryOperationType, TypeConfig<DiscoveryOperationStepName>>;

export const discoveryOperationStepConfigs = {
  discoverFieldsInitialize: {
    inputSchema: FieldDiscoveryInput,
    outputSchema: FieldDiscoveryInitializeResult,
  },
  discoverFieldsProcessBatch: {
    inputSchema: FieldDiscoveryProcessBatchOptions,
    outputSchema: FieldDiscoveryProcessBatchResult,
    isRepeatable: true,
  },
  discoverFieldsFinalize: {
    inputSchema: FieldDiscoveryFinalizeOptions,
    outputSchema: FieldDiscoveryFinalizeResult,
  },
  discoverNoteActionsInitialize: {
    inputSchema: NoteActionsDiscoveryInput,
    outputSchema: NoteActionsDiscoveryInitializeResult,
  },
  discoverNoteActionsProcessBatch: {
    inputSchema: NoteActionsDiscoveryProcessBatchOptions,
    outputSchema: NoteActionsDiscoveryProcessBatchResult,
    isRepeatable: true,
  },
  discoverNoteActionsFinalize: {
    inputSchema: NoteActionsDiscoveryFinalizeOptions,
    outputSchema: NoteActionsDiscoveryFinalizeResult,
  },
  discoverFieldExclusionsInitialize: {
    inputSchema: FieldExclusionsDiscoveryInput,
    outputSchema: FieldExclusionsInitializeResult,
  },
  discoverFieldExclusionsProcessEntity: {
    inputSchema: FieldExclusionsProcessEntityOptions,
    outputSchema: FieldExclusionsProcessEntityResult,
    isRepeatable: true,
  },
  discoverFieldExclusionsFinalize: {
    inputSchema: FieldExclusionsFinalizeOptions,
    outputSchema: FieldExclusionsFinalizeResult,
  },
} as const satisfies Record<DiscoveryOperationStepName, StepConfig>;
